import { Graphics, TextStyle } from 'pixi.js';
import { Input } from '@pixi/ui';
import { COLORS, FONTS, LAYOUT } from '../constants/theme';

export interface UIInputOptions {
  width: number;
  height?: number;
  placeholder?: string;
  value?: string;
  maxLength?: number;
  borderColor?: number;
  onChange?: (value: string) => void;
  onEnter?: (value: string) => void;
}

/**
 * Reusable text input component using @pixi/ui Input
 * Replaces DOM-based text inputs for consistency
 */
export class UIInput extends Input {
  private inputOptions: UIInputOptions;
  private onChangeConnection: ((text: string) => void) | null = null;
  private onEnterConnection: ((text: string) => void) | null = null;

  constructor(options: UIInputOptions) {
    const {
      width,
      height = 40,
      placeholder = '',
      value = '',
      maxLength = 12,
      borderColor = COLORS.PLAYER1,
    } = options;

    // Create background graphics
    const bg = new Graphics();
    bg.roundRect(0, 0, width, height, LAYOUT.BORDER_RADIUS);
    bg.fill({ color: COLORS.BG_TERTIARY });
    bg.stroke({ color: borderColor, width: 2 });

    // Create text style
    const textStyle: Partial<TextStyle> = {
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.MD,
      fill: COLORS.TEXT_PRIMARY,
    };

    super({
      bg,
      textStyle,
      placeholder,
      value,
      maxLength,
      padding: { top: 10, right: 10, bottom: 10, left: 10 },
      align: 'left',
      addMask: true,
    });

    this.inputOptions = options;

    // Connect signals
    if (options.onChange) {
      this.onChangeConnection = options.onChange;
      this.onChange.connect(this.onChangeConnection);
    }

    if (options.onEnter) {
      this.onEnterConnection = options.onEnter;
      this.onEnter.connect(this.onEnterConnection);
    }
  }

  /**
   * Get the current input value
   */
  getValue(): string {
    return this.value;
  }

  /**
   * Set the input value
   */
  setValue(text: string): void {
    this.value = text;
  }

  /**
   * Enable/disable the input
   */
  setDisabled(disabled: boolean): void {
    this.eventMode = disabled ? 'none' : 'static';
    this.alpha = disabled ? 0.6 : 1;
  }

  /**
   * Update the border color
   */
  setBorderColor(color: number): void {
    const width = this.inputOptions.width;
    const height = this.inputOptions.height ?? 40;

    const newBg = new Graphics();
    newBg.roundRect(0, 0, width, height, LAYOUT.BORDER_RADIUS);
    newBg.fill({ color: COLORS.BG_TERTIARY });
    newBg.stroke({ color, width: 2 });

    this.bg = newBg;
  }

  /**
   * Clean up signal connections
   */
  override destroy(): void {
    if (this.onChangeConnection) {
      this.onChange.disconnect(this.onChangeConnection);
      this.onChangeConnection = null;
    }
    if (this.onEnterConnection) {
      this.onEnter.disconnect(this.onEnterConnection);
      this.onEnterConnection = null;
    }
    super.destroy();
  }
}
